import type {
  RiskDimension,
  RiskFramework,
  ScoreBand,
  SignalSeverity,
} from "@/api";
import type { BadgeTone } from "@/components/ui/badge";

/** Bahasa Indonesia label per score band. The backend enum has
 *  four values (critical / needs_attention / good / excellent) —
 *  not five as the FE-3B spec sketched. */
export const SCORE_BAND_LABEL: Record<ScoreBand, string> = {
  critical: "Kritis",
  needs_attention: "Butuh perhatian",
  good: "Baik",
  excellent: "Sangat baik",
};

export const SCORE_BAND_TONE: Record<ScoreBand, BadgeTone> = {
  critical: "danger",
  needs_attention: "warning",
  good: "success",
  excellent: "ocean",
};

export const DIMENSION_LABEL: Record<RiskDimension, string> = {
  security: "Keamanan",
  policy: "Kebijakan",
  asset: "Aset",
  compliance: "Kepatuhan",
};

export const SIGNAL_SEVERITY_LABEL: Record<SignalSeverity, string> = {
  low: "Rendah",
  medium: "Sedang",
  high: "Tinggi",
  critical: "Kritis",
};

export const SIGNAL_SEVERITY_TONE: Record<SignalSeverity, BadgeTone> = {
  low: "neutral",
  medium: "ocean",
  high: "warning",
  critical: "danger",
};

/** Pretty-prints a backend framework slug. The framework enum uses
 *  underscore-snake-case; the UI prefers spaces. */
export const FRAMEWORK_LABEL: Record<RiskFramework | string, string> = {
  NIST_AI_RMF: "NIST AI RMF",
  ISO_42001: "ISO/IEC 42001",
  ISO_27001: "ISO/IEC 27001",
  EU_AI_ACT: "EU AI Act",
  UU_PDP_2022: "UU PDP 2022",
  POJK_11_2022: "POJK 11/2022",
  PP_71_2019: "PP 71/2019",
};

export function formatFramework(framework: string): string {
  return FRAMEWORK_LABEL[framework] ?? framework;
}

/** Compute a signed delta between two scores. Returns null when
 *  either value is missing or not finite — the dashboard then
 *  shows a neutral indicator instead of a fake zero. */
export function calculateDelta(
  current: number | string | null | undefined,
  previous: number | string | null | undefined
): number | null {
  const cur = typeof current === "string" ? Number(current) : current;
  const prev = typeof previous === "string" ? Number(previous) : previous;
  if (
    cur === null ||
    cur === undefined ||
    prev === null ||
    prev === undefined ||
    !Number.isFinite(cur) ||
    !Number.isFinite(prev)
  ) {
    return null;
  }
  return cur - prev;
}

/** Compute the band derived from a numeric score using the
 *  methodology thresholds. Defaults to the backend defaults
 *  (critical < 40, needs_attention < 60, good < 80, excellent >=
 *  80) so the FE can label a score even before the methodology
 *  config has been fetched. */
export function deriveScoreBand(
  score: number | string | null | undefined,
  thresholds?: {
    threshold_critical?: number;
    threshold_needs_attention?: number;
    threshold_good?: number;
  }
): ScoreBand | null {
  const value =
    typeof score === "string"
      ? Number(score)
      : score === null || score === undefined
        ? NaN
        : score;
  if (!Number.isFinite(value)) return null;
  const tCritical = thresholds?.threshold_critical ?? 40;
  const tWarn = thresholds?.threshold_needs_attention ?? 60;
  const tGood = thresholds?.threshold_good ?? 80;
  if (value < tCritical) return "critical";
  if (value < tWarn) return "needs_attention";
  if (value < tGood) return "good";
  return "excellent";
}

/** Round a numeric (or numeric-string) score to one decimal place
 *  and add the trailing ``%``. Used by the dashboard tile + tables
 *  so every render lands on the same precision. */
export function formatScore(
  score: number | string | null | undefined
): string {
  if (score === null || score === undefined) return "—";
  const value = typeof score === "string" ? Number(score) : score;
  if (!Number.isFinite(value)) return "—";
  return `${value.toFixed(1)}`;
}
